import { useCallback, useEffect, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { load, save } from '../lib/storage.js';
import { sendOrQueue } from '../lib/outbox.js';
import { num } from '../lib/format.js';

/**
 * Material-Auswahl eines Auftrags: Artikel antippen → Menge → in die Auswahl. Gebucht wird erst mit «Speichern»,
 * alles in einem Aufruf (POST …/material/sammel), immer als echter Artikel (bzw. Freitext, wenn so gewählt).
 * Die Auswahl liegt pro Auftrag auf dem Gerät (unter «bs.», beim Abmelden gelöscht) und übersteht Suchen,
 * Seitenwechsel und Neuladen.
 *
 * Eintrag: { key, artikelId|null, freitext, titel, nummer, einheit, bildUrl, menge, fehler? }
 */
export function useAuswahl(nr) {
  const qc = useQueryClient();
  const speicherKey = `auswahl.${nr}`;
  const [items, setItems] = useState(() => load(speicherKey, []));
  const [busy, setBusy] = useState(false);

  useEffect(() => { setItems(load(speicherKey, [])); }, [speicherKey]);
  useEffect(() => { save(speicherKey, items.length ? items : null); }, [speicherKey, items]);

  const mengeVon = useCallback((artikelId) => items.find((i) => i.artikelId === String(artikelId))?.menge ?? 0, [items]);

  /** Menge eines Artikels setzen (0 = aus der Auswahl). */
  const setArtikel = useCallback((artikel, menge) => {
    const id = String(artikel.id);
    setItems((list) => {
      const rest = list.filter((i) => i.artikelId !== id);
      if (!(menge > 0)) return rest;
      const alt = list.find((i) => i.artikelId === id);
      const item = { key: `a${id}`, artikelId: id, freitext: false, titel: artikel.titel, nummer: artikel.nummer ?? '', einheit: artikel.einheit ?? 'Stk', bildUrl: artikel.bildUrl ?? null, menge };
      return alt ? list.map((i) => (i.artikelId === id ? item : i)) : [...list, item];
    });
  }, []);

  const addFreitext = useCallback((titel, menge) => {
    setItems((list) => [...list, { key: `f${Date.now()}`, artikelId: null, freitext: true, titel, nummer: '', einheit: 'Stk', bildUrl: null, menge }]);
  }, []);

  const setMenge = useCallback((key, menge) => {
    setItems((list) => (menge > 0 ? list.map((i) => (i.key === key ? { ...i, menge, fehler: undefined } : i)) : list.filter((i) => i.key !== key)));
  }, []);

  const leeren = useCallback(() => setItems([]), []);

  /**
   * Alles buchen. Ohne Netz in die Outbox (wird automatisch nachgesendet). Positionen, die Exxas ablehnt,
   * bleiben mit Fehlertext in der Auswahl.
   * @returns {Promise<{ gebucht: number, fehler: number, vorgemerkt: boolean }>}
   */
  const speichern = useCallback(async () => {
    const list = items;
    if (!list.length || busy) return { gebucht: 0, fehler: 0, vorgemerkt: false };
    setBusy(true);
    try {
      const positionen = list.map((i) => (i.freitext ? { freitext: true, titel: i.titel, menge: i.menge } : { artikelId: i.artikelId, menge: i.menge }));
      const r = await sendOrQueue({
        kind: 'material',
        nummer: nr,
        uid: qc.getQueryData(['me'])?.uid ?? null,
        label: list.length === 1 ? `${num(list[0].menge)} ${list[0].einheit} ${list[0].titel}` : `${list.length} Material-Positionen`,
        path: `/auftraege/${encodeURIComponent(nr)}/material/sammel`,
        body: { positionen },
      });
      if (r.queued) {
        setItems([]);
        return { gebucht: 0, fehler: 0, vorgemerkt: true };
      }
      const ergebnisse = r.res?.ergebnisse ?? [];
      const offen = list
        .map((i, n) => (ergebnisse[n]?.ok ? null : { ...i, fehler: ergebnisse[n]?.fehler ?? 'nicht gebucht' }))
        .filter(Boolean);
      setItems(offen);
      // Positionen direkt aus dem Ergebnis nachführen – ohne die Liste neu bei Exxas zu holen
      qc.setQueryData(['material', nr], (alt) => {
        if (!alt) return alt; // noch nie angesehen → wird beim Ansehen geladen
        const neu = alt.map((p) => ({ ...p }));
        list.forEach((i, n) => {
          const e = ergebnisse[n];
          if (!e?.ok) return;
          const pos = neu.find((p) => (e.positionId && p.id === String(e.positionId)) || (!i.freitext && p.artikelId === i.artikelId && !p.freitext));
          if (pos && !i.freitext) { pos.menge = e.menge; if (e.positionId) pos.id = String(e.positionId); }
          else neu.push({ id: String(e.positionId ?? `neu-${n}`), posNr: null, artikelId: i.freitext ? null : i.artikelId, freitext: i.freitext, titel: i.titel, bezeichnung: i.nummer ?? '', menge: e.menge ?? i.menge, einheit: i.einheit });
        });
        return neu;
      });
      return { gebucht: list.length - offen.length, fehler: offen.length, vorgemerkt: false };
    } finally {
      setBusy(false);
    }
  }, [items, busy, nr, qc]);

  return { items, mengeVon, setArtikel, addFreitext, setMenge, leeren, speichern, busy };
}
